import { useEffect, useRef, useState } from "react";

import api from "../../lib/api";
import { Input } from "../ui/Input";

interface ClienteOption {
  id: number;
  razonSocial?: string;
  cif?: string | null;
  emailContacto?: string | null;
  email?: string | null;
}

interface ClientesResponse {
  data: ClienteOption[];
}

export function ClienteSearchSelect({
  value,
  onChange,
  label = "Cliente",
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ClienteOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const previousValue = useRef(value);

  useEffect(() => {
    if (previousValue.current && !value && !open) setQuery("");
    previousValue.current = value;
  }, [open, value]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    const timer = window.setTimeout(() => {
      api
        .get<ClientesResponse>("/admin/clientes", {
          params: {
            page: 1,
            limit: 20,
            search: query.trim() || undefined,
          },
        })
        .then((response) => {
          if (!cancelled) setOptions(response.data.data ?? []);
        })
        .catch(() => {
          if (!cancelled) {
            setOptions([]);
            setLoadError(true);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const selectClient = (client: ClienteOption) => {
    onChange(String(client.id));
    setQuery(client.razonSocial?.trim() || `Cliente #${client.id}`);
    setOpen(false);
  };

  return (
    <div className="relative w-full">
      <Input
        label={label}
        placeholder="Buscar por empresa, CIF o email"
        value={query}
        role="combobox"
        aria-expanded={open}
        aria-controls="admin-cliente-resultados"
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          if (!value) setQuery("");
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
          } else if (event.key === "Enter" && open && options.length === 1) {
            event.preventDefault();
            selectClient(options[0]);
          }
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          if (value) onChange("");
        }}
      />

      {open && (
        <div
          id="admin-cliente-resultados"
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
        >
          <button
            type="button"
            role="option"
            aria-selected={!value}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange("");
              setQuery("");
              setOpen(false);
            }}
            className="block w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
          >
            Todos los clientes
          </button>

          {loading ? (
            <p className="px-3 py-3 text-sm text-gray-500">Buscando clientes…</p>
          ) : loadError ? (
            <p className="px-3 py-3 text-sm text-red-600">
              No se pudieron buscar los clientes.
            </p>
          ) : options.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-500">
              No hay clientes que coincidan con la búsqueda.
            </p>
          ) : (
            options.map((client) => (
              <button
                key={client.id}
                type="button"
                role="option"
                aria-selected={value === String(client.id)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectClient(client)}
                className="block w-full rounded-md px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              >
                <span className="block text-sm font-medium text-gray-900">
                  {client.razonSocial?.trim() || `Cliente #${client.id}`}
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  {[client.cif, client.emailContacto, client.email]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
